// B19 (Day 29) — locale-aware internal link helper.
//
// Internal links across the SPA are authored with TR segments
// ("/tr/film/...", "/tr/tur/...", "/tr/haber/...", "/tr/ara/..."). When the
// active locale differs from TR, this helper rewrites the path to the active
// locale's canonical segments via localizeUrl (film->movie, tur->genre,
// haber->news, ulkeler->countries, ...), so cards, sliders, search results
// and navigation no longer drop non-TR users back onto the Turkish page.
//
// The active locale is read from the server-injected bootstrap data
// (`i18n.language`), which is correct during SSR, client hydration and
// Googlebot JS execution alike. TR locale is a no-op (zero regression).

import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {localizeUrl} from './localize-url';
import {TR_TO_TARGET} from './route-translations';
import {DEFAULT_LOCALE, isAppLocale, type AppLocale} from './locale-config';

export function activeLocale(): AppLocale {
  const lang = getBootstrapData()?.i18n?.language;
  return isAppLocale(lang) ? (lang as AppLocale) : DEFAULT_LOCALE;
}

/**
 * BLOK 3d — Tek bir TR URL segment'ini (sezon/bolum/...) aktif locale'in
 * canonical segment'ine çevirir. season/episode linkleri başlık önekini
 * localize edip ardından TR segment'i DÜZ ekliyordu → non-TR'de hibrit URL
 * (ör. /es/serie/house/sezon/1/bolum/1) hiçbir rotaya uymuyor, 404 veriyordu.
 * Bununla /es → temporada/episodio, /en → season/episode üretilir.
 */
export function localizedSegment(trSegment: string): string {
  const locale = activeLocale();
  if (locale === 'tr') return trSegment;
  return TR_TO_TARGET[locale as Exclude<AppLocale, 'tr'>]?.[trSegment] ?? trSegment;
}

/**
 * Translate a TR-segment internal path to the active locale. Only paths that
 * start with "/tr" are rewritten; anything else (already-localized, external,
 * hash/relative) is returned untouched.
 */
export function localizeTo(path: string): string {
  const locale = activeLocale();
  if (locale !== 'tr' && /^\/tr(\/|$)/.test(path)) {
    return localizeUrl(path, locale);
  }
  return path;
}
